// Per-row UI for a recipe inside the duplicate-finder panel. Recipes
// don't have a heavyweight edit modal accessible from Inventory (their
// full editor lives in the Models panel), so v1 exposes:
//   - Inline rename (text input + save) -- the common cleanup move
//     (distinguish duplicate-named recipes from each other).
//   - Deactivate (soft delete via updateManufacturingRecipe).
//
// Hard delete is intentionally absent: recipes can be referenced by
// model BOM lines and recipe counts, so we never let the UI hard-delete.
import { useState } from "react";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { mfgMicroInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  recipe: MfgRecipeSummary;
  busy: boolean;
  canDeactivate?: boolean;
  onRename: (
    recipeId: string,
    nextName: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  onDeactivate: (
    recipeId: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
};

const saveBtnClass =
  "rounded-md border border-indigo-300 bg-white px-2 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-700 dark:bg-zinc-800 dark:text-indigo-300 dark:hover:bg-indigo-950/30";

const dangerBtnClass =
  "rounded-md border border-red-300 bg-white px-2 py-0.5 text-[11px] text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-700 dark:bg-zinc-800 dark:text-red-300 dark:hover:bg-red-950/40";

export function ManufacturingRecipeDuplicateRow({
  recipe,
  busy,
  canDeactivate = false,
  onRename,
  onDeactivate,
}: Props) {
  const [draftName, setDraftName] = useState(recipe.name);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = draftName.trim() !== recipe.name.trim();

  async function handleSave() {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    const r = await onRename(recipe.id, draftName.trim());
    setSaving(false);
    if (!r.ok) setError(r.error);
  }

  async function handleDeactivate() {
    if (deactivating || !recipe.is_active) return;
    if (!window.confirm(`Deactivate recipe "${recipe.name}"?`)) return;
    setDeactivating(true);
    setError(null);
    const r = await onDeactivate(recipe.id);
    setDeactivating(false);
    if (!r.ok) setError(r.error);
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            className={mfgMicroInputClass}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            disabled={busy || saving}
          />
          <button
            type="button"
            className={saveBtnClass}
            onClick={() => void handleSave()}
            disabled={!dirty || busy || saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="mt-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          {recipe.code} · {recipe.kind}
          {recipe.is_active ? "" : " · inactive"}
        </div>
        {error ? (
          <div className="mt-0.5 text-[10px] text-red-700 dark:text-red-300">{error}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {canDeactivate ? (
          <button
            type="button"
            className={dangerBtnClass}
            onClick={() => void handleDeactivate()}
            disabled={busy || deactivating || !recipe.is_active}
            title={
              recipe.is_active
                ? "Mark this recipe inactive (soft delete)"
                : "Already inactive"
            }
          >
            {deactivating ? "Deactivating…" : recipe.is_active ? "Deactivate" : "Inactive"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
